import React, { useState, useEffect, useMemo } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { getSizeBands } from './helpers';
import { titleCaseColor } from '../../lib/utils';

/**
 * Combined multi-colour + per-colour-size selector for fast SO entry.
 *
 * Opens (auto on Tab for multi-colour designs, or on click) with all colours
 * listed A–Z, each with a checkbox. Tick a colour → its size grid appears
 * inline (tick sizes + set qty). A "Qty each" field fills every ticked size
 * across every ticked colour, and each size's qty can still be fine-tuned.
 * On "Done" the parent creates ONE order row per selected colour.
 */
const MultiColorSizePopover = ({ idx, row, sizesForColor, applyMultiColor, large, autoOpen, onAutoOpenConsumed }) => {
  const [open, setOpen] = useState(false);
  const [extraColors, setExtraColors] = useState([]);
  const [adding, setAdding] = useState(false);
  const [qtyEach, setQtyEach] = useState('');
  // selected = { [color]: { [size]: qtyString } } — only ticked colours/sizes
  const [selected, setSelected] = useState({});

  const disabled = !row.product_id;

  const colors = useMemo(() => {
    const all = [...(row.colors || []), ...extraColors];
    return [...new Set(all)].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [row.colors, extraColors]);

  useEffect(() => {
    if (autoOpen && !disabled) {
      setOpen(true);
      onAutoOpenConsumed && onAutoOpenConsumed(idx);
    }
    // eslint-disable-next-line
  }, [autoOpen]);

  // Seed from the row's current colour/sizes whenever the popup opens (lets you
  // re-open and edit an already-built row).
  useEffect(() => {
    if (!open) return;
    if (row.color) {
      setSelected({ [row.color]: { ...(row.sizeQty || {}) } });
    } else {
      setSelected({});
    }
    setExtraColors(prev => (row.color && !(row.colors || []).includes(row.color) && !prev.includes(row.color) ? [...prev, row.color] : prev));
    setQtyEach('');
    setAdding(false);
    // eslint-disable-next-line
  }, [open]);

  const toggleColor = (c) => setSelected(prev => {
    const next = { ...prev };
    if (c in next) delete next[c]; else next[c] = {};
    return next;
  });

  const toggleSize = (c, size) => setSelected(prev => {
    const sq = { ...(prev[c] || {}) };
    if (size in sq) delete sq[size]; else sq[size] = qtyEach || '1';
    return { ...prev, [c]: sq };
  });

  const setSizeQty = (c, size, val) => setSelected(prev => ({ ...prev, [c]: { ...(prev[c] || {}), [size]: val } }));

  const applyBand = (c, band, sizes) => setSelected(prev => {
    const sq = { ...(prev[c] || {}) };
    const allSel = band.sizes.every(s => s in sq);
    if (allSel) band.sizes.forEach(s => delete sq[s]);
    else band.sizes.forEach(s => { if (sizes.some(x => x.size === s)) sq[s] = qtyEach || '1'; });
    return { ...prev, [c]: sq };
  });

  const selectAllSizes = (c, sizes) => setSelected(prev => {
    const sq = { ...(prev[c] || {}) };
    sizes.forEach(s => { if (!(s.size in sq)) sq[s.size] = qtyEach || '1'; });
    return { ...prev, [c]: sq };
  });

  const clearColorSizes = (c) => setSelected(prev => ({ ...prev, [c]: {} }));

  // "Qty each" fills every currently-ticked size across all ticked colours and
  // becomes the default for newly ticked sizes.
  const applyQtyEach = (val) => {
    setQtyEach(val);
    if (val === '') return;
    setSelected(prev => {
      const next = {};
      for (const [c, sq] of Object.entries(prev)) {
        next[c] = {};
        Object.keys(sq).forEach(s => { next[c][s] = val; });
      }
      return next;
    });
  };

  const addColor = (name) => {
    const c = titleCaseColor(name);
    setAdding(false);
    if (!c) return;
    setExtraColors(prev => (prev.includes(c) ? prev : [...prev, c]));
    setSelected(prev => ({ ...prev, [c]: {} }));
  };

  const handleDone = () => {
    const selections = Object.entries(selected)
      .map(([color, sq]) => ({
        color,
        sizeQty: Object.fromEntries(Object.entries(sq).filter(([, q]) => parseInt(q) > 0).map(([s, q]) => [s, parseInt(q)])),
      }))
      .filter(sel => Object.keys(sel.sizeQty).length > 0);
    applyMultiColor(idx, selections);
    setOpen(false);
  };

  const totalPcs = Object.values(selected).reduce((sum, sq) => sum + Object.values(sq).reduce((a, q) => a + (parseInt(q) || 0), 0), 0);
  const tickedColors = Object.keys(selected).filter(c => Object.keys(selected[c] || {}).length > 0).length;
  const triggerLabel = row.color || 'Select';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          data-testid={`so-color-trigger-${idx}`}
          className={`${large ? 'h-11 text-base' : 'h-8 text-sm'} w-full rounded-md border px-2 text-left flex items-center justify-between ${disabled ? 'bg-gray-50 text-gray-300 cursor-not-allowed' : 'bg-background hover:border-sky-400'} ${row.color ? 'border-sky-300 text-sky-800 font-medium' : 'border-input text-gray-500'}`}
        >
          <span className="truncate">{triggerLabel}</span>
          <svg className="w-3.5 h-3.5 ml-1 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0 z-50" align="start" data-testid={`so-color-popover-${idx}`}>
        <div className="p-2 border-b bg-gray-50/80 flex items-center gap-2">
          <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide flex-shrink-0">Qty each</span>
          <Input type="number" min="0" value={qtyEach} placeholder="fills all ticked sizes"
            onChange={e => applyQtyEach(e.target.value)} onWheel={e => e.target.blur()}
            className="h-7 text-sm flex-1" data-testid={`so-mc-qty-each-${idx}`} />
        </div>

        <div className="max-h-72 overflow-y-auto p-1.5 space-y-1">
          {colors.length === 0 && <div className="px-3 py-2 text-xs text-gray-400">No catalogue colours — add one below.</div>}
          {colors.map(c => {
            const checked = c in selected;
            const sizes = checked ? sizesForColor(c) : [];
            const bands = checked ? getSizeBands(sizes) : [];
            const sq = selected[c] || {};
            const colorPcs = Object.values(sq).reduce((a, q) => a + (parseInt(q) || 0), 0);
            return (
              <div key={c} className={`rounded-md border ${checked ? 'border-sky-200 bg-sky-50/40' : 'border-transparent'}`}>
                <label className="flex items-center gap-2 px-2 py-1.5 cursor-pointer" data-testid={`so-mc-color-${c}-${idx}`}>
                  <Checkbox checked={checked} onCheckedChange={() => toggleColor(c)} data-testid={`so-mc-color-check-${c}-${idx}`} />
                  <span className={`text-sm flex-1 ${checked ? 'font-semibold text-sky-800' : 'text-gray-700'}`}>{c}</span>
                  {checked && colorPcs > 0 && <span className="text-xs text-emerald-700 font-medium">{colorPcs} pcs</span>}
                </label>
                {checked && (
                  <div className="px-2 pb-2 space-y-1.5">
                    <div className="flex flex-wrap gap-1">
                      {bands.map(b => {
                        const active = b.sizes.every(s => s in sq);
                        return (
                          <Button key={b.id} type="button" size="sm" variant={active ? 'default' : 'outline'}
                            className={`h-6 px-2 text-xs ${active ? 'bg-sky-600 hover:bg-sky-700' : 'border-sky-300 text-sky-700 hover:bg-sky-50'}`}
                            onClick={() => applyBand(c, b, sizes)} data-testid={`so-mc-band-${b.id}-${c}-${idx}`}>{b.label}</Button>
                        );
                      })}
                      <Button type="button" size="sm" variant="outline" className="h-6 px-2 text-xs" onClick={() => selectAllSizes(c, sizes)} data-testid={`so-mc-all-${c}-${idx}`}>All</Button>
                      <Button type="button" size="sm" variant="ghost" className="h-6 px-2 text-xs text-red-500 hover:bg-red-50" onClick={() => clearColorSizes(c)} data-testid={`so-mc-clear-${c}-${idx}`}>Clear</Button>
                    </div>
                    <div className="grid grid-cols-1 gap-1">
                      {sizes.length === 0 && <span className="text-[11px] text-gray-400">No sizes for this colour.</span>}
                      {sizes.map(s => {
                        const sChecked = s.size in sq;
                        return (
                          <div key={s.size} className="flex items-center gap-2">
                            <Checkbox checked={sChecked} onCheckedChange={() => toggleSize(c, s.size)} data-testid={`so-mc-size-${c}-${s.size}-${idx}`} />
                            <span className="text-xs w-12 text-gray-600">{s.size}</span>
                            <Input type="number" min="0" value={sChecked ? (sq[s.size] ?? '') : ''} disabled={!sChecked}
                              onChange={e => setSizeQty(c, s.size, e.target.value)} onWheel={e => e.target.blur()}
                              className="h-7 w-16 text-right text-xs" placeholder="qty" data-testid={`so-mc-size-qty-${c}-${s.size}-${idx}`} />
                            <span className="text-[11px] text-gray-400">@ {s.rate || '—'}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {adding ? (
            <div className="p-1.5">
              <Input autoFocus placeholder="Colour name + Enter" className="h-8 text-sm" data-testid={`so-mc-addcolor-input-${idx}`}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addColor(e.target.value); } else if (e.key === 'Escape') { setAdding(false); } }}
                onBlur={e => addColor(e.target.value)} />
            </div>
          ) : (
            <button type="button" onClick={() => setAdding(true)} className="w-full text-left px-3 py-2 rounded text-sm text-sky-600 hover:bg-sky-50" data-testid={`so-mc-addcolor-${idx}`}>+ Add colour…</button>
          )}
        </div>

        <div className="flex items-center justify-between p-2 border-t bg-gray-50/80">
          <span className="text-xs text-gray-500">{tickedColors} colour{tickedColors === 1 ? '' : 's'} · {totalPcs} pcs</span>
          <Button type="button" size="sm" className="h-7 bg-sky-600 hover:bg-sky-700" onClick={handleDone} data-testid={`so-mc-done-${idx}`}>Done</Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default MultiColorSizePopover;
